import React, { forwardRef } from 'react';
import { cx } from '../../utils/cx.js';
import { FOCUS_RING } from './Button';

const VARIANTS = {
  ghost: 'text-gray-500 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-white',
  outline: 'border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-900 dark:border-gray-700 dark:bg-night-800 dark:text-gray-400 dark:hover:bg-night-750 dark:hover:text-white',
  danger: 'text-gray-500 hover:bg-red-50 hover:text-red-600 dark:text-gray-400 dark:hover:bg-red-500/10 dark:hover:text-red-400',
  overlay: 'bg-white/10 text-white backdrop-blur hover:bg-white/20',
};

const ACTIVE = 'border-brand-200 bg-brand-50 text-brand-600 dark:border-brand-800 dark:bg-brand-900 dark:text-brand-300';

const SIZES = {
  xs: { box: 'h-7 w-7 rounded-md', icon: 'h-3.5 w-3.5' },
  sm: { box: 'h-8 w-8 rounded-lg', icon: 'h-4 w-4' },
  md: { box: 'h-9 w-9 rounded-lg', icon: 'h-[18px] w-[18px]' },
  lg: { box: 'h-12 w-12 rounded-full', icon: 'h-6 w-6' },
};

/** Square icon-only button. `label` is required: it is the accessible name and the tooltip. */
const IconButton = forwardRef(({
  icon: Icon,
  label,
  variant = 'ghost',
  size = 'sm',
  active = false,
  spin = false,
  className = '',
  ...rest
}, ref) => {
  const sz = SIZES[size] || SIZES.sm;
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      title={label}
      className={cx(
        'inline-flex flex-shrink-0 items-center justify-center transition-colors cursor-pointer',
        'disabled:cursor-not-allowed disabled:opacity-40',
        FOCUS_RING,
        sz.box,
        active ? cx('border', ACTIVE) : VARIANTS[variant] || VARIANTS.ghost,
        className,
      )}
      {...rest}
    >
      <Icon className={cx(sz.icon, spin && 'animate-spin')} aria-hidden />
    </button>
  );
});

IconButton.displayName = 'IconButton';

export default IconButton;
